// Red de seguridad global (portada de PILOT). Si cualquier componente truena
// al pintarse, React desmonta TODO el árbol y la pantalla queda EN BLANCO —
// sin mensaje, sin menú, sin salida. Esto atrapa ese error y enseña algo
// digno: qué pasó y un botón para recargar.
//
// Los error boundaries son la única pieza de React que los hooks no cubren:
// solo una CLASE con getDerivedStateFromError atrapa errores de render.
import { Component, type ErrorInfo, type ReactNode } from "react";
import { enSubdirectorio } from "../lib/rutas";

/**
 * ¿Es el error de un fragmento de código que ya no existe? Pasa cuando se
 * publica una versión nueva y una pestaña abierta pide el archivo viejo: el
 * navegador recibe otra cosa (o nada) y el import dinámico revienta.
 * La cura es recargar para tomar la versión nueva.
 */
function esFragmentoViejo(e: unknown): boolean {
  const m = String((e as Error)?.message ?? e).toLowerCase();
  return (
    m.includes("dynamically imported module") ||
    m.includes("failed to fetch dynamically") ||
    m.includes("importing a module script failed") ||
    m.includes("expected a javascript") ||
    m.includes("mime type")
  );
}

// Guarda CUÁNDO se recargó, no un simple "1". Con un "1" que se limpiaba al
// montar bien, la bandera se borraba SIEMPRE (montar es justo lo que pasa tras
// recargar), así que un fragmento que siguiera faltando dejaba la app
// recargándose EN BUCLE sin mostrar nunca el error. Es el mismo tropiezo que
// PILOT documenta en su main.jsx y arregla con sello de tiempo.
// Con la marca: si vuelve a fallar dentro del minuto, ya no recarga y sale la
// tarjeta de error. Pasado ese minuto (otro día, otro deploy) tiene otro intento.
export const BANDERA_RECARGA = "radar_chunk_recargado";
export const VENTANA_RECARGA_MS = 60000;

/** ¿Se puede intentar la recarga, o ya se intentó hace nada? */
export function puedeRecargarPorFragmento(): boolean {
  try {
    const marca = Number(sessionStorage.getItem(BANDERA_RECARGA) || 0);
    if (marca && Date.now() - marca < VENTANA_RECARGA_MS) return false;
    sessionStorage.setItem(BANDERA_RECARGA, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}

interface Props { children: ReactNode }
interface State { error: Error | null; donde: string | null }

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, donde: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, donde: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Radar — error de render:", error, info?.componentStack ?? "");
    // DOS pistas distintas y las dos importan: el stack del ERROR apunta a la
    // línea exacta del lanzamiento (en el chunk que sea); el de COMPONENTES
    // solo dice en qué componente cayó. Con la primera y los sourcemaps se
    // traduce a archivo:línea del código real.
    // si el blindaje del build descartó un cleanup inválido, ese registro es
    // la pista número uno — va al frente
    const rota = (window as unknown as { __fabricaRota?: { valor: string } }).__fabricaRota;
    const stackError = (rota ? `DESCARTADO ${rota.valor} || ` : "") + (error?.stack ?? "")
      .split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 4).join(" ‹ ");
    const pila = (info?.componentStack ?? "")
      .split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 2).join(" ‹ ");
    this.setState({ donde: ["LANZADO " + stackError, "COMPONENTE " + pila].filter(Boolean).join("  ||  ") });

    // Versión nueva publicada mientras la pestaña estaba abierta: recargar,
    // pero solo si no se intentó hace menos de un minuto.
    if (esFragmentoViejo(error) && puedeRecargarPorFragmento()) {
      window.location.reload();
    }
  }

  render() {
    const { error, donde } = this.state;
    if (!error) return this.props.children;

    const detalle = String(error?.message ?? error);
    const viejo = esFragmentoViejo(error);

    return (
      <div id="login">
        <div className="login-card">
          <h1>Radar KEX</h1>
          <p>
            {viejo
              ? "Se publicó una versión nueva mientras tenías esto abierto."
              : "Algo se rompió al pintar esta pantalla."}
          </p>
          <div className="error" style={{ wordBreak: "break-word" }}>{detalle}</div>
          {/* El componente donde tronó: sin esto un "x is not a function" no
              dice NADA de dónde vino, y hay que adivinar. */}
          {donde && (
            <div className="error" style={{ wordBreak: "break-word", fontSize: 12, opacity: .85 }}>
              en: {donde}
            </div>
          )}
          <button className="btn" type="button" onClick={() => window.location.reload()}>
            Recargar
          </button>
          {/* Salida de emergencia al Radar viejo, que hoy vive en la raíz
              mientras la beta cuelga de /beta. El día del switch esta app SERÁ
              la de la raíz: el enlace apuntaría a sí mismo y no habría a dónde
              escapar, así que desaparece solo (BASE_URL pasa a ser "/"). */}
          {enSubdirectorio && (
            <p style={{ margin: "14px 0 0", textAlign: "center", fontSize: 13 }}>
              El Radar de siempre sigue funcionando en{" "}
              <a href="/" style={{ color: "var(--s2)" }}>radar.kex.com.mx</a>.
            </p>
          )}
        </div>
      </div>
    );
  }
}
